import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useConsommation } from '../api/queries';
import { Carte } from '../components/Carte';
import { Selecteur } from '../components/Champs';
import { MessageErreur } from '../components/MessageErreur';
import { Tableau, type Colonne } from '../components/Tableau';
import { formaterNombre } from '../lib/format';
import { formaterOctets } from './ClientsPage';

/**
 * Consommation de données du parc.
 *
 * Ce que cet écran sert vraiment : savoir ce que coûtent les cartes SIM, et repérer le boîtier
 * qui dérive. D'où le choix des trois vues — un total pour la facture, une série pour la
 * tendance, deux classements pour l'anomalie.
 *
 * La série est tracée en SVG, sans bibliothèque de graphes : une courbe à trente points ne
 * justifie pas 40 ko de dépendance, et le back-office est déjà cartographié de la même façon.
 */

const OPTIONS_FENETRE = [
  { valeur: '7', libelle: '7 jours' },
  { valeur: '30', libelle: '30 jours' },
  { valeur: '90', libelle: '90 jours' },
  { valeur: '365', libelle: '12 mois' },
];

interface LigneAppareil {
  numero_serie_electrificateur: string;
  nom: string | null;
  exploitation_nom: string | null;
  octets: number;
  trames: number;
  trames_rejetees: number;
}

export function ConsommationPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const jours = parametres.get('jours') ?? '30';

  const { data, error, isPending } = useConsommation({ jours: Number(jours) });

  const serie = data?.serie ?? [];
  const maximum = useMemo(
    () => Math.max(1, ...serie.map((p) => p.octets_montants + p.octets_descendants)),
    [serie],
  );

  const colonnes: Colonne<LigneAppareil>[] = [
    {
      cle: 'appareil',
      entete: 'Appareil',
      rendu: (a) => (
        <div>
          <div style={{ fontWeight: 600 }}>{a.nom ?? a.numero_serie_electrificateur}</div>
          <div style={{ fontSize: 12, color: '#6B7480' }}>
            {a.numero_serie_electrificateur}
            {a.exploitation_nom ? ` · ${a.exploitation_nom}` : ''}
          </div>
        </div>
      ),
    },
    { cle: 'octets', entete: 'Volume', numerique: true, rendu: (a) => formaterOctets(a.octets) },
    { cle: 'trames', entete: 'Trames', numerique: true, rendu: (a) => formaterNombre(a.trames) },
    {
      cle: 'rejetees',
      entete: 'Rejetées',
      numerique: true,
      // Une trame rejetée consomme de la donnée sans rien apporter : c'est le signe le plus
      // direct d'un firmware défectueux, et il se lit ici et nulle part ailleurs.
      rendu: (a) =>
        a.trames_rejetees === 0 ? (
          <span style={{ color: '#6B7480' }}>—</span>
        ) : (
          <span style={{ color: '#D0342C', fontWeight: 600 }}>
            {formaterNombre(a.trames_rejetees)}
          </span>
        ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, flex: 1 }}>Consommation de données</h1>
        <Selecteur
          label="Fenêtre"
          options={OPTIONS_FENETRE}
          value={jours}
          onChange={(e) => {
            const suivants = new URLSearchParams(parametres);
            suivants.set('jours', e.currentTarget.value);
            setParametres(suivants, { replace: true });
          }}
        />
      </div>

      {error ? <MessageErreur erreur={error} /> : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 12,
        }}
      >
        <Tuile libelle="Volume total" valeur={formaterOctets(data?.total.octets_total ?? 0)} />
        <Tuile libelle="Montant" valeur={formaterOctets(data?.total.octets_montants ?? 0)} />
        <Tuile libelle="Descendant" valeur={formaterOctets(data?.total.octets_descendants ?? 0)} />
        <Tuile
          libelle="Par appareil / jour"
          valeur={formaterOctets(data?.total.octets_moyens_par_appareil_jour ?? 0)}
          aide="Comparable à un forfait SIM"
        />
        <Tuile
          libelle="Appareils actifs"
          valeur={formaterNombre(data?.total.appareils_actifs ?? 0)}
        />
        <Tuile
          libelle="Trames rejetées"
          valeur={formaterNombre(data?.total.trames_rejetees ?? 0)}
          accent={(data?.total.trames_rejetees ?? 0) > 0 ? '#D0342C' : undefined}
        />
      </div>

      <Carte titre="Volume quotidien">
        {serie.length === 0 ? (
          <p style={{ color: '#6B7480', margin: 0 }}>
            {isPending ? 'Chargement…' : 'Aucune donnée sur cette fenêtre.'}
          </p>
        ) : (
          <svg
            viewBox={`0 0 ${Math.max(serie.length * 14, 100)} 120`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: 160, display: 'block' }}
            role="img"
            aria-label="Volume quotidien de données"
          >
            {serie.map((p, i) => {
              const total = p.octets_montants + p.octets_descendants;
              const hauteur = Math.max(1, (total / maximum) * 104);
              return (
                <rect
                  key={p.jour}
                  x={i * 14 + 3}
                  y={112 - hauteur}
                  width={8}
                  height={hauteur}
                  rx={2}
                  fill="#16733A"
                >
                  <title>{`${p.jour} — ${formaterOctets(total)} (${p.trames} trames)`}</title>
                </rect>
              );
            })}
            <line x1="0" y1="112" x2={serie.length * 14} y2="112" stroke="#E3E7EC" strokeWidth="1" />
          </svg>
        )}
      </Carte>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
        <Carte titre="Appareils les plus bavards">
          <Tableau
            colonnes={colonnes}
            lignes={(data?.par_appareil ?? []) as LigneAppareil[]}
            cleDe={(a) => a.numero_serie_electrificateur}
            chargement={isPending}
            titreVide="Aucun appareil"
            descriptionVide="Aucune trame reçue sur cette fenêtre."
          />
        </Carte>

        <Carte titre="Par client">
          <Tableau
            colonnes={[
              { cle: 'nom', entete: 'Client', rendu: (c: { nom: string }) => c.nom },
              {
                cle: 'appareils',
                entete: 'Appareils',
                numerique: true,
                rendu: (c: { appareils: number }) => formaterNombre(c.appareils),
              },
              {
                cle: 'octets',
                entete: 'Volume',
                numerique: true,
                rendu: (c: { octets: number }) => formaterOctets(c.octets),
              },
            ]}
            lignes={data?.par_client ?? []}
            cleDe={(c) => c.id}
            chargement={isPending}
            titreVide="Aucun client"
            descriptionVide="Aucune consommation sur cette fenêtre."
          />
        </Carte>
      </div>
    </div>
  );
}

function Tuile({
  libelle,
  valeur,
  accent,
  aide,
}: {
  libelle: string;
  valeur: string;
  accent?: string;
  aide?: string;
}): JSX.Element {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E3E7EC',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <div style={{ fontSize: 12, color: '#6B7480', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {libelle}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ?? '#14181F', marginTop: 4 }}>
        {valeur}
      </div>
      {aide ? <div style={{ fontSize: 11, color: '#9AA3AE', marginTop: 2 }}>{aide}</div> : null}
    </div>
  );
}
